import { createEcho } from '@/services/echo'

export type RealtimeUnsubscribe = () => void

export interface RealtimeChannel {
  listen: (event: string, callback: CallableFunction) => RealtimeChannel
  notification: (callback: CallableFunction) => RealtimeChannel
  stopListening: (event: string, callback?: CallableFunction) => RealtimeChannel
}

export interface RealtimeClient {
  channel: (name: string) => RealtimeChannel
  private: (name: string) => RealtimeChannel
  leaveChannel: (name: string) => void
  disconnect: () => void
}

type RealtimeClientFactory = (token: string) => RealtimeClient
type ChannelKind = 'public' | 'private'

interface Subscription {
  id: number
  kind: ChannelKind
  channel: string
  event: string
  callback: CallableFunction
  notification: boolean
}

const NOTIFICATION_EVENT = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated'

export class RealtimeService {
  private client: RealtimeClient | null = null
  private token = ''
  private userId: number | null = null
  private nextSubscriptionId = 1
  private readonly subscriptions = new Map<number, Subscription>()
  private readonly createClient: RealtimeClientFactory

  constructor (createClient: RealtimeClientFactory = (token) => createEcho(token) as unknown as RealtimeClient) {
    this.createClient = createClient
  }

  connect (token: string, userId: number): void {
    if (!token || !userId) {
      this.disconnect()
      return
    }

    if (this.client && this.token === token && this.userId === userId) {
      return
    }

    this.disconnect()
    this.token = token
    this.userId = userId
    this.client = this.createClient(token)
    this.subscriptions.forEach(subscription => this.attach(subscription))
  }

  disconnect (): void {
    if (this.client) {
      this.client.disconnect()
    }

    this.client = null
    this.token = ''
    this.userId = null
  }

  listenPublic (channel: string, event: string, callback: CallableFunction): RealtimeUnsubscribe {
    return this.register({ kind: 'public', channel, event, callback, notification: false })
  }

  listenPrivate (channel: string, event: string, callback: CallableFunction): RealtimeUnsubscribe {
    return this.register({ kind: 'private', channel, event, callback, notification: false })
  }

  listenPrivateNotification (channel: string, callback: CallableFunction): RealtimeUnsubscribe {
    return this.register({
      kind: 'private',
      channel,
      event: NOTIFICATION_EVENT,
      callback,
      notification: true
    })
  }

  private register (subscription: Omit<Subscription, 'id'>): RealtimeUnsubscribe {
    const registeredSubscription: Subscription = {
      ...subscription,
      id: this.nextSubscriptionId++
    }

    this.subscriptions.set(registeredSubscription.id, registeredSubscription)
    this.attach(registeredSubscription)

    return () => this.unregister(registeredSubscription.id)
  }

  private unregister (subscriptionId: number): void {
    const subscription = this.subscriptions.get(subscriptionId)
    if (!subscription) {
      return
    }

    if (this.client) {
      this.getChannel(subscription).stopListening(subscription.event, subscription.callback)
    }

    this.subscriptions.delete(subscriptionId)

    if (this.client && !this.hasChannelConsumers(subscription)) {
      this.client.leaveChannel(this.resolvedChannelName(subscription))
    }
  }

  private attach (subscription: Subscription): void {
    if (!this.client) {
      return
    }

    const channel = this.getChannel(subscription)
    if (subscription.notification) {
      channel.notification(subscription.callback)
    } else {
      channel.listen(subscription.event, subscription.callback)
    }
  }

  private getChannel (subscription: Subscription): RealtimeChannel {
    if (!this.client) {
      throw new Error('Realtime client is niet verbonden.')
    }

    return subscription.kind === 'private'
      ? this.client.private(subscription.channel)
      : this.client.channel(subscription.channel)
  }

  private hasChannelConsumers (subscription: Subscription): boolean {
    return Array.from(this.subscriptions.values()).some(candidate => (
      candidate.kind === subscription.kind && candidate.channel === subscription.channel
    ))
  }

  private resolvedChannelName (subscription: Subscription): string {
    return subscription.kind === 'private'
      ? `private-${subscription.channel}`
      : subscription.channel
  }
}

export const realtimeService = new RealtimeService()
